/* eslint-disable no-nested-ternary */
import React, {memo, useEffect} from 'react';

// material-ui
import {Avatar, Box, Chip, Dialog, IconButton, Toolbar, Tooltip} from '@mui/material';

// assets
import PrintIcon from '@mui/icons-material/PrintOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';


// project imports
import MainCard from 'ui-component/cards/MainCard';
import InvoicePreview, {canIssueReceiptFromInvoice} from './invoice/InvoicePreview';
import DocumentNew from './DocumentNew';
import DeliveryCertificatePreview from './deliveryCertificate/DeliveryCertificatePreview';
import ReceiptPreview from './receipt/ReceiptPreview';

// Locale
import {FormattedMessage} from 'react-intl';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// types
import {ExtendedDocument, isSupplierDocument, ProductNote} from 'types/DocumentsTypes/Document';
import {PayNote} from '../../../../types/DocumentsTypes/PayNote';
import {TaxNote} from '../../../../types/DocumentsTypes/TaxNote';
import {
    buildSupNotesSum,
    getDocumentPdf,
    getDocumentPhoto,
    getDocumentSign,
    documentPhotoCount,
    MAX_DOC_PHOTOS,
    getMassDocumentPdf,
    massSendFilesOf,
    sendPdfEmail
} from '../../../../api/customer/documents.api';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import {printPdf, saveByteArray, roundDecimals} from '../../../../fromKotlin/nkutils';
import {useActivateCustomerDocument, useUpdateCustomerDocument, refreshDocs, SUPPLIER_DOCUMENTS, documentAfterCancel} from '../../../../hooks/customer/documents.hooks';
import {pdf} from '../../../../assets/images';
import {getFile} from '../../../../api/customer/files.api';
import DebtPreview from './debt/DebtPreview';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import JSDB from '../../../../fromKotlin/nk';
import ShowImageDialog, {FieldPhotoCameraIcon} from '../../../../ui-component/dialogs/showImageDialog';
import {useTheme} from '@mui/material/styles';
import IItem from '../../../../types/product_delivery';
import './style.css';
import GenerativeLoader from '../../../../ui-component/GenerativeLoader';
import CancelNoteDialog from '../../../../ui-component/dialogs/cancelNoteDialog';
import {Email, MoneyOutlined, Payment as PaymentIcon, WhatsApp} from '@mui/icons-material';
import VisitPreview from "./visit/VisitPreview";
import {filterZeroPayments, Payment} from 'types/DocumentsTypes/utils';
import InvoiceAndReciptPreview from './invoice/InvoiceAndReceiptPreview';
import ApproveNoteDialog from "../../../../ui-component/dialogs/approveChangeNote";
import SaveSharpIcon from "@mui/icons-material/SaveSharp";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import SendMessageDialog from "../../../../ui-component/dialogs/sendMessage";
import {useAiPageContext} from '../../../../hooks/useAiAssistant';
import OrderConflictDialog from '../../../orders/ordersNew/OrderConflictDialog';
import {createOrderData, writeOrdersToOD} from '../../../../types/OrderData';
import {getOrdersByIds} from '../../../../api/customer/documents.api';
import {getTranzilaConfig} from '../../../../api/customer/tranzila.api';
import {paymentSendFromDocs} from '../../../../utils/paymentLinkDocs';

function nPriceField(x: number | undefined | null): number {
    if (x == null || Number.isNaN(Number(x))) return 0;
    return roundDecimals(Number(x), 4);
}

function documentEntity(doc: ExtendedDocument) {
    const id = Number(doc?.entId);
    if (!id) return null;
    return isSupplierDocument(doc.type)
        ? JSDB().getSupplier(id)?.first
        : JSDB().getClient(id)?.first;
}

function sameProductLinePrices(a: IItem, b: IItem): boolean {
    if (nPriceField(a.totalPrice) !== nPriceField(b.totalPrice)) return false;
    if (nPriceField(a.discount) !== nPriceField(b.discount)) return false;
    if (nPriceField(a.costPrice) !== nPriceField(b.costPrice)) return false;
    if (nPriceField(a.totalPriceInclude) !== nPriceField(b.totalPriceInclude)) return false;
    if (nPriceField(a.totalPriceExclude) !== nPriceField(b.totalPriceExclude)) return false;
    const na = a.nkObject ?? {};
    const nb = b.nkObject ?? {};
    if (nPriceField(na.price) !== nPriceField(nb.price)) return false;
    if (Number(na.use_price ?? 0) !== Number(nb.use_price ?? 0)) return false;
    if (Number(na.use_price_tax_note ?? 0) !== Number(nb.use_price_tax_note ?? 0)) return false;
    return true;
}

/** When true, product-line price fields match the loaded document (qty/notes may still differ). */
function productListPricesUnchanged(
    original: IItem[] | undefined | null,
    edited: IItem[] | undefined | null,
): boolean {
    const o = original ?? [];
    const e = edited ?? [];
    if (e.length === 0 && o.length === 0) return true;
    if (e.length !== o.length) return false;
    for (const line of e) {
        const match = o.find(
            (p) => p.id === line.id && (p.notePlacement ?? -1) === (line.notePlacement ?? -1),
        );
        if (!match || !sameProductLinePrices(match, line)) return false;
    }
    return true;
}

/** 0 = מקור, 1 = העתק, 2 = מקור+העתק (originForce 3, same as datasheet context menu) */
type OriginPrintMode = 0 | 1 | 2;

const ORIGIN_CHIP_LABELS: Record<OriginPrintMode, string> = {
    0: 'מקור',
    1: 'העתק',
    2: 'מקור+העתק',
};

function fetchDocumentPdf(doc: ExtendedDocument, mode: OriginPrintMode): Promise<any> {
    if (mode === 2) {
        return getMassDocumentPdf([doc], undefined, undefined, undefined, 3);
    }
    return getDocumentPdf(doc, mode === 0);
}

const DocumentPreview = ({
                             document,
                             ExtraOptions,
                             setSelectedCustomersSend,
                             pool,
                             outerDoc,
                            collect,
                            onCanceled,
                            onUpdated
                         }: { document?: ExtendedDocument; ExtraOptions?: () => JSX.Element, creditRecepitValue: boolean, setCreditRecepitValue: React.Dispatch<React.SetStateAction<boolean>>, setSelectedCustomersSend?: React.Dispatch<any> ,pool?:string, outerDoc?: any,collect?:boolean, onCanceled?: (original: ExtendedDocument, result?: ExtendedDocument | null) => void, onUpdated?: (doc: ExtendedDocument) => void}) => {
    // let [doc,setDoc] = React.useState<ExtendedDocument|undefined>(document);
    let doc = document
    const aiDocumentContext = React.useMemo(() => document ? ({
        page: 'document.preview',
        summary: `${document.type || 'document'} #${document.id || ''}`.trim(),
        data: {
            document: {
                id: document.id,
                type: document.type,
                status: document.status,
                entity_id: (document as any).entId ?? (document as any)?.nkObject?.client_id,
                entity_name: (document as any).entName,
            },
        },
    }) : null, [document]);
    // Every native DocumentPreview (not only one opened from the AI bridge)
    // tells the supervisor which exact ERP document the user is viewing.
    useAiPageContext(aiDocumentContext, Boolean(document), 20);
    console.log(pool)
    const createCustomerDocument = useActivateCustomerDocument(pool ?? (document?.type && isSupplierDocument(document?.type)?'SUPPLIER_DOCUMENTS' :undefined));
    const isPayDocument = doc?.type === 'receipt' || doc?.type === 'sup_receipt';
    const updateCustomerDocument = useUpdateCustomerDocument(doc?.type && isSupplierDocument(doc.type) ? 'supplier' : 'client', isPayDocument ? 1 : doc?.type=='visit_note'?2:0,pool);
    const refresher = refreshDocs(pool);
    const [selectedCustomersSend2, setSelectedCustomersSend2] = React.useState<any>(undefined)
    const setSelectedCustomersSend3 = setSelectedCustomersSend ?? setSelectedCustomersSend2;
    const [docFromBot,setDocFromBot] = React.useState<ExtendedDocument | undefined>(undefined);
    const documentRef = React.useRef(document);
    documentRef.current = document;
    const docFromBotRef = React.useRef(docFromBot);
    docFromBotRef.current = docFromBot;
    const onUpdatedRef = React.useRef(onUpdated);
    onUpdatedRef.current = onUpdated;
    const applyRefreshedSourceDoc = React.useCallback(async () => {
        const current = docFromBotRef.current ?? documentRef.current;
        if (!current?.id || !current?.nkObject?.getConnectedDocType) return;
        try {
            const refreshedDocs = await refresher.mutateAsync({
                ids: [Number(current.id)],
                types: [current.nkObject.getConnectedDocType()],
                offline: false,
                full: true
            });
            const refreshed = (refreshedDocs as ExtendedDocument[] | undefined)?.find(
                (d) => Number(d.id) === Number(current.id) && d.type === current.type
            );
            if (refreshed) {
                setDocFromBot(refreshed);
                onUpdatedRef.current?.(refreshed);
            }
        } catch (e) {
            console.log(e);
        }
    }, [refresher]);
    if (doc) {

        // customerSpecificDoc= useGetSpecificCustomerDocuments(doc.type,undefined,undefined,undefined,undefined,doc.id,true);

        // React.useEffect(() => {
        //     customerSpecificDoc.refetch()
        //
        // }, []);

        // doc = (createCustomerDocument.data as ExtendedDocument)?? (customerSpecificDoc.data as ExtendedDocument[])[0]
        doc = (createCustomerDocument.data as ExtendedDocument) ?? doc;
        doc = (updateCustomerDocument.data as ExtendedDocument) ?? doc;
        // Latest explicit live copy (receipt issued, payment cancelled, invoice credited, …)
        // wins over sticky mutation `.data`. A tax cancel returns a new credit, so match
        // the document that was opened, not that credit.
        if (docFromBot && document && docFromBot.id == document.id && docFromBot.type == document.type) {
            doc = docFromBot;
        } else if (docFromBot && docFromBot.id == doc.id && docFromBot.type == doc.type) {
            doc = docFromBot;
        }

    }


    if (!doc)
        return <Box/>;

    // React.useEffect(() => {
    //     const genProds = async () => {
    //         const temp = await getSpecificDocument(doc.type, undefined, undefined, undefined, undefined, doc.id, true, undefined);
    //         setDoc(temp[0])
    //
    //     };
    //     if(doc) {
    //         setDoc((createCustomerDocument.data as ExtendedDocument) ?? doc)
    //         setDoc((updateCustomerDocument.data as ExtendedDocument) ?? doc)
    //     }
    //     if ((doc.type == 'delivery_certificate' || doc.type =='order') && (doc as ProductNote).products.length == 0)
    //         genProds();
    //
    // }, []);
    const icons = doc.type !== 'debt' && doc.nkObject !== undefined;
    const entity = documentEntity(doc);
    const canCancel = doc.status !== 'canceled' && (doc.status !== 'closed' || doc.type === 'receipt' || doc.type === 'tax_invoice' ||  doc.type=='visit_note' || doc.type === 'sup_receipt' || doc.type === 'sup_tax_invoice')
    const canActivate =  doc?.status === 'canceled' && (doc?.type === 'delivery_certificate' ||doc?.type=="order");
    const isVisitClientOrder = doc.type === 'order'
        && JSDB().getClient(Number(doc.entId)).first?.getNotes() === -1;
    const canEdit = (doc.type === 'delivery_certificate' && doc.status === 'open') || (doc.type == 'order' && (doc.status != 'closed' || isVisitClientOrder)) || (doc.type == 'supplier_note' && doc.status !== 'canceled') || (doc.type === 'receipt') || (doc.type === "visit_note") ||  (doc.type === "tax_invoice") || (doc.type === "refund")|| (doc.type === "order" && doc.status === "open") || (doc.type === "invoice" ) || (doc.type === "sup_invoice") || (doc.type === "sup_tax_invoice") || (doc.type === "sup_refund") || (doc.type === "sup_receipt");
    const withSign = doc.sign;
    const [withPhoto, setWithPhoto] = React.useState<boolean>(doc?.photo ? true : false);
    const [photoCount, setPhotoCount] = React.useState<number>(documentPhotoCount(doc));
    const [products, setProducts] = React.useState<IItem[]>([]);
    const [internalEdit, setInternalEdit] = React.useState<any>(doc);
    React.useEffect(() => {
        setInternalEdit(doc)
        setWithPhoto(!!doc?.photo);
        setPhotoCount(documentPhotoCount(doc));
    }, [doc]);
    const [payments, setPayments] = React.useState<Payment[]>([]);
    const [withDiscount, setWithDiscount] =React.useState<boolean>(parseInt(localStorage.getItem('withDiscount') ?? '0') == 1);
    const [editting, setEditting] = React.useState<boolean>(false);
    const [originPrintMode, setOriginPrintMode] = React.useState<OriginPrintMode>(0);
    const [send, setSend] = React.useState(false);
    const [priceApproveOpen, setPriceApproveOpen] = React.useState(false);
    const [receiptOpen, setReceiptOpen] = React.useState(false);
    const canCreateReceipt = canIssueReceiptFromInvoice(doc) && !editting;
    React.useEffect(() => {
        if (!editting) setPriceApproveOpen(false);
    }, [editting]);
    const setStartEdit = React.useCallback(() => {
        if (doc?.type === 'delivery_certificate' || doc?.type === 'order' || doc?.type === 'supplier_note' || doc?.type === 'invoice' || doc?.type === 'sup_invoice' || doc?.type === 'refund' || doc?.type === 'sup_refund') {
            setEditting(true);
            setProducts((doc as ProductNote).products.map((p) => {
                return {
                    ...p
                };
            }));
        } else if (doc?.type === 'receipt' || doc?.type === 'sup_receipt') {
            setEditting(true);
            setPayments((doc as PayNote).payments.map((p) => {
                return {
                    ...p
                };
            }));
        } else if (doc?.type === "tax_invoice" || doc?.type === "sup_tax_invoice") {
            setEditting(true);
            setProducts((doc as ProductNote).products.map((p) => {
                return {
                    ...p
                };
            }));
            setPayments((doc as PayNote).payments.map((p) => {
                return {
                    ...p
                };
            }));
        } else if (doc?.type === "visit_note") {
            setEditting(true);
        }
    }, [doc]);
    // --- FIX: Accept index and update by index ---
    const onProductChangeMain = React.useCallback((product: IItem, idx?: number) => {
        setProducts((prevProducts) => {
            if (typeof idx === 'number') {
                // Update by index
                return prevProducts.map((p, i) => (i === idx ? product : p));
            }
            // fallback: update by id+notePlacement
            let updated = false;
            const x = prevProducts.map((p) => {
                if (product.id === p.id && (product.notePlacement !== undefined && p.notePlacement === product.notePlacement)) {
                    updated = true;
                    return product;
                }
                return p;
            });
            if (!updated) {
                x.push(product);
            }
            return x;
        });
    }, []);
    // Pass index to onProductChange
    const onProductChange = (editting)
        ? (product: IItem, idx?: number) => onProductChangeMain(product, idx)
        : undefined;
    // New-invoice forms pass their own product callback on the draft document.
    // Keep that autocomplete. A saved client invoice does not get one.
    const previewProductChange = (doc as any).onProductChange
        ?? (isSupplierDocument(doc.type) ? onProductChange : undefined);
    const onPaymentChangeMain = React.useCallback(
        (payment: Payment) => setPayments((prevPayments) => prevPayments.map((pmt) => (payment.id === pmt.id ? payment : pmt))),
        []
    );
    const onPaymentChange = (editting) ? onPaymentChangeMain : undefined;

    const invoiceAndRecepitDoc = {
        ...doc, payments: ((editting) ?
            (payments) : (doc as PayNote).payments),
        products: ((editting) ?
            (products) : (doc as ProductNote).products)
    };

    // VERSION_CONFLICT on an order edit: someone else saved this order after it was opened here.
    // Instead of a generic error we load the server copy and let the user compare / decide.
    const [orderConflict, setOrderConflict] = React.useState<{ payload: any; server: any } | null>(null);
    const saveDocument = React.useCallback((payload: any) => {
        updateCustomerDocument.mutate(payload, {
            onError: async (e: any) => {
                const conflict = e?.name === 'VERSION_CONFLICT' || String(e?.message || '').includes('VERSION_CONFLICT');
                if (doc.type !== 'order' || !conflict) return;
                let server: any = null;
                try {
                    server = (await getOrdersByIds([Number(doc.id)]))[0] ?? null;
                } catch {
                    server = null;
                }
                if (!server) {
                    Swal.fire({
                        title: 'ההזמנה עודכנה בינתיים על ידי משתמש אחר',
                        text: 'לא ניתן לטעון את הגרסה מהשרת — רענן ונסה שוב',
                        icon: 'warning',
                        target: '#custom-target',
                        confirmButtonColor: '#d63030'
                    });
                    return;
                }
                setEditting(true);
                setOrderConflict({payload, server});
            },
        });
    }, [updateCustomerDocument, doc.type, doc.id]);

    const handleSaveEdit = React.useCallback(() => {
        const editedProductsForSave = doc.type === 'visit_note' ? internalEdit?.products : products;
        const baselineProducts = (doc as ProductNote)?.products;
        const mustShowPriceDialog =
            withDiscount &&
            JSDB().getUser().first.note_price != 4 &&
            !productListPricesUnchanged(baselineProducts, editedProductsForSave);
        const paymentsToSave = isPayDocument || doc.type === 'tax_invoice' || doc.type === 'sup_tax_invoice'
            ? filterZeroPayments(payments)
            : payments;

        if (!mustShowPriceDialog) {
            saveDocument({
                ...internalEdit,
                products: doc.type === 'visit_note' ? internalEdit.products : products,
                payments: paymentsToSave,
                preserveExistingUsePrice: true,
            });
            setEditting(false);
        } else {
            setPriceApproveOpen(true);
        }
    }, [doc.type, doc, internalEdit, products, payments, withDiscount, saveDocument, isPayDocument]);

    // "mine" for the compare dialog: an OrderData built from the payload we tried to save.
    const conflictMine = React.useMemo(() => {
        if (!orderConflict || doc.type !== 'order') return null;
        try {
            const od = createOrderData({
                orderId: String(doc.id),
                id: String(doc.entId ?? ''),
                customer: String((doc as any).entName ?? ''),
                readonly: false,
            });
            writeOrdersToOD(od, orderConflict.payload as ProductNote);
            return od;
        } catch (e) {
            console.log(e);
            return null;
        }
    }, [orderConflict, doc]);

    // "base" for the compare dialog: the order as it was opened here (pre-edit), so the dialog can
    // tag each differing row with who changed it (me / the other user / both).
    const conflictBase = React.useMemo(() => {
        if (!orderConflict || doc.type !== 'order') return null;
        try {
            const od = createOrderData({
                orderId: String(doc.id),
                id: String(doc.entId ?? ''),
                customer: String((doc as any).entName ?? ''),
                readonly: false,
            });
            writeOrdersToOD(od, doc as ProductNote);
            return od;
        } catch (e) {
            console.log(e);
            return null;
        }
    }, [orderConflict, doc]);

    const conflictTakeMine = React.useCallback(() => {
        const payload = orderConflict?.payload;
        setOrderConflict(null);
        if (!payload) return;
        // getOrdersByIds refreshed the lib cache with the server row_version → this write passes the lock.
        saveDocument(payload);
        setEditting(false);
    }, [orderConflict, saveDocument]);

    const conflictTakeServer = React.useCallback(() => {
        const server = orderConflict?.server;
        setOrderConflict(null);
        setEditting(false);
        if (!server) return;
        setDocFromBot(server);
        setInternalEdit(server);
        onUpdatedRef.current?.(server);
        void applyRefreshedSourceDoc();
    }, [orderConflict, applyRefreshedSourceDoc]);

    return (
        <Box id='custom-target'>
            <MainCard>
                {(icons) ? <Toolbar
                    sx={{
                        p: 0,
                        pl: 1,
                        pr: 1
                    }}
                >
                    <Box sx={{flexGrow: 1}}/>
                    {ExtraOptions && <ExtraOptions/>}

                    {canCreateReceipt ? (
                    <Tooltip title="הנפק קבלה">
                        <IconButton size='medium' style={{color: 'gray'}} onClick={() => setReceiptOpen(true)}>
                            <MoneyOutlined/>
                        </IconButton>
                    </Tooltip>
                    ) : null}

                    {(canEdit) ? <>

                    {!editting ? <Tooltip title={<FormattedMessage id='Edit'/>}>
                            <IconButton size='medium' style={{color: 'gray'}} onClick={(e) => {
                                setStartEdit();
                            }}>
                                <EditIcon/>
                            </IconButton>
                    </Tooltip> : (
                        <>
                            <IconButton size='medium' style={{color: 'red'}} onClick={handleSaveEdit}>
                                <SaveSharpIcon color='error' />
                            </IconButton>
                            <ApproveNoteDialog
                                hideTrigger
                                open={priceApproveOpen}
                                onClose={() => setPriceApproveOpen(false)}
                                approve={(specific, general) => {
                                    const paymentsToSave = isPayDocument || doc.type === 'tax_invoice' || doc.type === 'sup_tax_invoice'
                                        ? filterZeroPayments(payments)
                                        : payments;
                                    saveDocument({
                                        ...internalEdit,
                                        products: doc.type === 'visit_note' ? internalEdit.products : products,
                                        payments: paymentsToSave,
                                        savePriceInSpecificNote: specific,
                                        savePriceInGeneral: general,
                                    });
                                    setEditting(false);
                                }}
                            />
                            <OrderConflictDialog
                                open={!!orderConflict}
                                mine={conflictMine}
                                base={conflictBase}
                                server={orderConflict?.server}
                                onClose={() => setOrderConflict(null)}
                                onTakeMine={conflictTakeMine}
                                onTakeServer={conflictTakeServer}
                            />
                        </>
                    )}
                    </>

                        : undefined}
                    {withSign ? <ShowImageDialog calcFunction={() => {
                        return getDocumentSign(doc!);
                    }} icon={<BorderColorIcon/>} title={'חתימה בשטח'}
                                                 tooltip={'חתימה דיגיטלית'}
                                                 subtitle={`נחתם בתאריך ${doc.nkObject.signData().date} ע''י ${doc.nkObject.signData().signer} `}></ShowImageDialog> : undefined}

                    <ShowImageDialog
                    setWithPhoto={setWithPhoto}
                    pool={pool ?? (isSupplierDocument(doc.type) ? SUPPLIER_DOCUMENTS : undefined)}
                    onPhotoCountChange={(n) => {
                        setPhotoCount(n);
                        setWithPhoto(n > 0);
                    }}
                    calcFunction={() => {
                        return getDocumentPhoto(doc!);
                    }}
                    icon={<FieldPhotoCameraIcon count={photoCount}/>}
                    docs={[doc]}
                    title={'צילום בשטח'}
                    tooltip={photoCount > 0 ? `צילום בשטח (${photoCount}/${MAX_DOC_PHOTOS})` : "אין צילום"}
                    subtitle={withPhoto ? `צולם בתאריך ${doc?.nkObject?.signData()?.date ?? "לא יודע"}` : "אין צילום"}></ShowImageDialog>

                    {doc?.entId ? <Tooltip title={'שלח אימייל'}>
                        <GenerativeLoader disabled={!entity?.getEmail?.()}
                                          func={(setLoader) => {
                                              getFile(false, () => fetchDocumentPdf(doc!, originPrintMode), `${doc!.title} - ${doc!.entName}`, undefined, undefined, setLoader, async (b, t) => {
                                                  const email = entity?.getEmail?.()

                                                  if (email != "") {
                                                      const x = sendPdfEmail(b, email, t)
                                                      setSend(await x)
                                                      return x
                                                  } else {
                                                      return false
                                                  }
                                              });
                                          }} icon={<Email color={send ? 'success' : 'error'}
                                                          sx={{width: '24px', height: '24px'}}/>}/>

                    </Tooltip> : undefined}
                    {Number(doc?.entId) ? <Tooltip title={'שלח בוואטסאפ'}>
                        <GenerativeLoader
                        disabled={!entity?.phone || entity.phone.length < 10}
                                          func={(setLoader) => {
                                            //   getFile(false, () => getDocumentPdf(doc!, true), doc!.title, undefined, undefined, setLoader, async (b, t) => {
                                            //       const phone = JSDB().getClient(Number(doc!.entId)).first?.phone

                                            //       if (phone != "") {
                                            //         // ADD POHNE CALLBACK
                                            //         //   const x = sendPdfEmail(b, email, t)
                                            //         //   setSend(await x)
                                            //         //   return x
                                            //       } else {
                                            //           return false
                                            //       }
                                            //   });
                                              setSelectedCustomersSend3 && setSelectedCustomersSend3({
                                                selected: [doc?.entId],
                                                defaultPlatform: 'whatsapp',
                                                files: massSendFilesOf([doc!]),
                                                msg: '#docs',
                                                subject: 'שליחת מסמכים',
                                            })
                                          }} icon={<WhatsApp
                                                //color={send ? 'success' : 'disabled'}
                                                sx={{
                                                    width: '24px',
                                                    height: '24px',
                                                    color: "#25D366"
                                                }}
                                                          />}/>

                    </Tooltip> : undefined}
                    {Number(doc?.entId) && !isSupplierDocument(doc.type) && getTranzilaConfig() ? (
                        <Tooltip title={'תשלום בלינק'}>
                            <GenerativeLoader
                                disabled={!entity?.phone || entity.phone.length < 10}
                                func={() => {
                                    const payload = paymentSendFromDocs([doc!]);
                                    if (!payload.selected.length) {
                                        Swal.fire({
                                            icon: 'info',
                                            title: 'אין יתרה לתשלום',
                                            text: 'אין סכום פתוח על המסמך לשליחת קישור תשלום.',
                                        });
                                        return;
                                    }
                                    setSelectedCustomersSend3 && setSelectedCustomersSend3(payload);
                                }}
                                icon={<PaymentIcon sx={{width: '24px', height: '24px', color: '#128C7E'}}/>}
                            />
                        </Tooltip>
                    ) : undefined}

                    <Tooltip title={<FormattedMessage id='Download'/>}>
                        <GenerativeLoader func={(setLoader) => {
                            if (doc) {
                                getFile(false, () => fetchDocumentPdf(doc!, originPrintMode), `${doc.title} - ${doc.entName}`, "application/pdf", undefined, setLoader);
                            }

                        }} icon={<Avatar variant='square'
                                         sx={{cursor: 'pointer', bgcolor: 'white', width: '24px', height: '24px'}}
                                         src={pdf}/>}/>

                    </Tooltip>
                    <Tooltip title={<FormattedMessage id='Print'/>}>
                        <GenerativeLoader func={(setLoader) => {

                            if (doc) {
                                getFile(true, () => fetchDocumentPdf(doc!, originPrintMode), doc.title, undefined, undefined, setLoader);
                            }
                        }} icon={<PrintIcon color='primary' sx={{width: '24px', height: '24px'}}/>}/>

                    </Tooltip>
                    <Chip label={ORIGIN_CHIP_LABELS[originPrintMode]} chipcolor={'success'} size='small' onClick={() => {
                        setOriginPrintMode((prev) => ((prev + 1) % 3) as OriginPrintMode);
                    }}/>
                    {(canActivate) ? <Tooltip style={{marginRight: '20px'}} title='החזר מסמך' onClick={() => {
                        const newDoc = {...doc, status: 'open'};
                        createCustomerDocument.mutate({doc:newDoc});
                    }}>
                        <IconButton style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid #ccc',
                            borderRadius: '25px',
                            marginRight: '40px',
                            marginLeft: '-10px',
                            padding: '5px',
                        }} size='medium' >
                            <CheckCircleOutlineIcon color='success'/>
                            <span style={{
                                fontSize: '15px',
                                margin: '0 5px',
                            }}>החזר מסמך</span>
                        </IconButton>
                    </Tooltip> : undefined}
                    {canCancel ? <CancelNoteDialog
                        disabled={createCustomerDocument.isLoading}
                        curDate={(doc.type == 'delivery_certificate' || doc.type == 'order' || doc.type == 'supplier_note') ? undefined : doc.date.getMonth() != (new Date()).getMonth() ? new Date() : doc.date}
                        value_left={doc?.value_left}
                        cancelFunc={(date) => {
                            if (createCustomerDocument.isLoading) return;
                            const newDoc = {...doc, status: 'canceled' as const};
                            createCustomerDocument.mutate({doc: newDoc, changeDate: date}, {
                                onSuccess: (data) => {
                                    const shown = documentAfterCancel(doc, data);
                                    setDocFromBot(shown);
                                    onUpdatedRef.current?.(shown);
                                    onCanceled?.(newDoc as ExtendedDocument, data);
                                }
                            });
                        }
                        }/> : undefined}


                </Toolbar> : undefined}

                {(doc.type === 'receipt' || doc.type=='sup_receipt') ? (
                    <ReceiptPreview
                    creditRecepitValue={doc.creditRecepitValue}
                    setCreditRecepitValue={doc.setCreditRecepitValue}
                setDocFromBot = {setDocFromBot}
                     onPaymentChange={onPaymentChange}
                     setInternalEdit={setInternalEdit}
                     internalEdit={internalEdit}
                     onPaymentsChange={editting && isSupplierDocument(doc.type) ? setPayments : undefined}
                                    pool={pool}
                                    outerDoc={outerDoc}
                                    {...doc as PayNote}
                                    payments={((editting) ?
                                        (payments) : (doc as PayNote).payments)}/>
                ) : doc.type === 'delivery_certificate' || doc.type === 'order' || doc.type === 'supplier_note' ? (
                        <DeliveryCertificatePreview outerDoc={outerDoc} onProductChange={onProductChange}

                                                    {
                                                        ...doc as ProductNote
                                                    }
                                                    products={((editting) ?
                                                        (products) : (doc as ProductNote).products)}
                                                    setInternalEdit={setInternalEdit}
                                                    internalEdit={internalEdit}
                                                    editting={editting}
                                                    pool={pool}
                                                    collect={collect}
                                                    onProductsLoaded={editting ? setProducts : (doc as any).onProductsLoaded}
                        />
                    )
                    : doc.type === 'refund' || doc.type==='sup_refund' ? (
                        <InvoicePreview
                            outerDoc={outerDoc}
                            pool={pool}
                            setInternalEdit={setInternalEdit}
                            internalEdit={internalEdit}
                            {...doc as TaxNote}
                            products={isSupplierDocument(doc.type) && editting ? products : (doc as TaxNote).products}
                            onProductChange={previewProductChange}
                            editting={editting}
                            onCreateReceipt={canCreateReceipt ? () => setReceiptOpen(true) : undefined}
                            onInvoiceRefreshed={(refreshed) => {
                                setDocFromBot(refreshed);
                                onUpdated?.(refreshed);
                            }}/>
                    ) : doc.type === 'debt' ? (
                        <DebtPreview {...doc} />
                    ) : doc.type === 'visit_note' ? (
                        <VisitPreview {...doc} editting={editting} setInternalEdit={setInternalEdit} pool={pool}
                                      internalEdit={internalEdit}/>
                    ) : doc.type === 'tax_invoice' || doc.type==='sup_tax_invoice' ? (
                        <>
                            {/* <ReceiptPreview onPaymentChange={onPaymentChange}
                                {...doc as PayNote}
                                payments={((editting) ?
                                    (payments) : (doc as PayNote).payments)} />
                            <InvoicePreview {...doc as TaxNote} /> */}
                            <InvoiceAndReciptPreview outerDoc={outerDoc} onProductChange={onProductChange} pool={pool}             setInternalEdit={setInternalEdit}
                                                     internalEdit={internalEdit}  editting={editting}
                                                     onPaymentChange={onPaymentChange} {...invoiceAndRecepitDoc} />
                        </>
                    ) :doc.type === 'invoice' || doc.type==='sup_invoice' ? (
                        <InvoicePreview
                            outerDoc={outerDoc}
                            pool={pool}
                            setInternalEdit={setInternalEdit}
                            internalEdit={internalEdit}
                            {...doc as TaxNote}
                            products={isSupplierDocument(doc.type) && editting ? products : (doc as TaxNote).products}
                            onProductChange={previewProductChange}
                            editting={editting}
                            onCreateReceipt={canCreateReceipt ? () => setReceiptOpen(true) : undefined}
                            onInvoiceRefreshed={(refreshed) => {
                                setDocFromBot(refreshed);
                                onUpdated?.(refreshed);
                            }}/>
                    ):undefined }
            </MainCard>
            {selectedCustomersSend2!=undefined ? <SendMessageDialog
                open={selectedCustomersSend2!=undefined}
                setOpen={(open)=>setSelectedCustomersSend2(undefined)}

                toSelected={selectedCustomersSend2?.selected??[]}
                files={selectedCustomersSend2?.files??[]}
                msg={selectedCustomersSend2?.msg??""}
                subject={selectedCustomersSend2?.subject??""}
                defaultPlatform={selectedCustomersSend2?.defaultPlatform??"email"}
                withFiles={false}
                externalVar={selectedCustomersSend2?.externalVar??[]}

            /> :undefined}
            {receiptOpen ? (
                <Dialog
                    maxWidth="md"
                    fullWidth
                    open
                    onClose={async () => {
                        await applyRefreshedSourceDoc();
                        setReceiptOpen(false);
                    }}
                >
                    <DocumentNew
                        customerId={String(doc.entId)}
                        type={isSupplierDocument(doc.type) ? 'sup_receipt' : 'receipt'}
                        refId={[doc.id]}
                        types={[doc.type]}
                        onIssued={applyRefreshedSourceDoc}
                    />
                </Dialog>
            ) : undefined}
        </Box>
    );
};


export default memo(DocumentPreview);
